import { useTranslation } from "@privacy/translations";
import { Box, Divider, Stack } from "@mui/material";
import { Drawer } from "../../shared/Drawer";
import { DpiaIcon2 } from "../../shared/icons";
import { DateTimeInfo } from "../RegistrationsGridPage/RegistrationDrawer/DateTimeInfo";
import { usePrivacyNavigate } from "../../shared/usePrivacyNavigate";
import type { DpiaById } from "../../api/dpia.types";
import { DpiaActionMenu, useDpiaFormTabPath } from "./DpiaDrawerHeader";
import { DpiaStatusChip } from "./DpiaStatusChip";
import { DpiaStatusSelectExternal } from "./DpiaStatusSelectExternal";
import { dpiaFormQueryOptions, dpiaHistoryQueryOptions } from "../../api/dpia";
import { prefetch } from "../../api/prefetch";

type DpiaOverviewDrawerHeaderProps = {
  dpia: DpiaById;
  activeTab: "overview" | "history";
  onDrawerClose: () => void;
};

export const DpiaOverviewDrawerHeader = ({ dpia, activeTab, onDrawerClose }: DpiaOverviewDrawerHeaderProps) => {
  const { t } = useTranslation();
  const { paths } = usePrivacyNavigate();
  const formTabPath = useDpiaFormTabPath(dpia.id);

  return (
    <Drawer.Header
      onClose={onDrawerClose}
      status={dpia.canEdit ? "idle" : "readonly"}
      title={dpia.name}
      renderIcon={props => <DpiaIcon2 {...props} />}
      topRightSlot={
        <DpiaActionMenu dpiaId={dpia.id} onDeleted={onDrawerClose} ariaLabelKey="GridDrawer.Menu.ButtonLabel" />
      }
      bottomSlot={
        <>
          <Stack sx={{ px: Drawer.PADDING_X }}>
            <Drawer.TabButtons>
              <Drawer.TabButton active={activeTab === "overview"} to={paths.dpiaById(dpia.id)}>
                {t("Dynamic.Drawer.OverviewButton")}
              </Drawer.TabButton>
              {dpia.type !== "external" && (
                <Drawer.TabButton active={false} to={paths.dpiaPreAssessmentById(dpia.id)}>
                  {t("DpiaPreAssessment.PreAssessment")}
                </Drawer.TabButton>
              )}
              {dpia.type !== "external" && dpia.preAssessment.isCompleted && (
                <Drawer.TabButton active={false} to={formTabPath} onHover={() => prefetch(dpiaFormQueryOptions())}>
                  {t("RegistrationDrawer.Dpia")}
                </Drawer.TabButton>
              )}
              <Drawer.TabButton
                active={activeTab === "history"}
                to={paths.dpiaHistory(dpia.id)}
                onHover={() => prefetch(dpiaHistoryQueryOptions(dpia.id))}
              >
                {t("Dynamic.Drawer.HistoryButton")}
              </Drawer.TabButton>
            </Drawer.TabButtons>
          </Stack>
          {activeTab === "overview" && (
            <Box>
              <Divider />
              <Stack
                direction="row"
                sx={{ pb: 2, pt: 3, px: Drawer.PADDING_X, justifyContent: "space-between", alignItems: "flex-start" }}
              >
                <Stack sx={{ gap: 0.5 }}>
                  <DateTimeInfo label={t("DpiaDrawer.CreatedAt.Label")} datetime={dpia.createdAt} />
                  {dpia.updatedAt && (
                    <DateTimeInfo label={t("DpiaDrawer.ModifiedAt.Label")} datetime={dpia.updatedAt} />
                  )}
                </Stack>
                {dpia.type === "external" ? (
                  <DpiaStatusSelectExternal dpia={dpia} isReadOnly={!dpia.canEdit} />
                ) : (
                  dpia.status !== "preAssessment" && <DpiaStatusChip status={dpia.status} />
                )}
              </Stack>
            </Box>
          )}
        </>
      }
    />
  );
};
